import { useLayoutEffect, useState } from "react"

/**
 * An edit is open while a field holds what the planner is typing. Live sync keeps refreshing reads
 * underneath it, so once a colleague's change lands, a commit must be checked against the reads as
 * they stood before it (`useAsOfEditStart`), or it would overwrite a change the planner never saw.
 */
let openEdits = 0
// ponytail: one flag for every variant, so a colleague's edit anywhere stops an open edit adopting
// the planner's own late refresh too; note changed variants instead if that false conflict shows up.
let colleagueChanged = false

/** Opens an edit; the returned function closes it, once. */
export function beginEdit(): () => void {
  if (openEdits++ === 0) colleagueChanged = false
  let closed = false
  return () => {
    if (closed) return
    closed = true
    if (--openEdits === 0) colleagueChanged = false
  }
}

/** A colleague's change is landing: open edits stop adopting refreshed reads. */
export function noteColleagueChange(): void {
  if (openEdits > 0) colleagueChanged = true
}

/**
 * Refreshes before a colleague's change are adopted, even mid-edit: the planner's own commit
 * landing late must not read as a conflict, and a first load must not leave the edit unchecked.
 */
export function trackAsOfEdit<T>(initial: T): { update: (value: T) => void; get: () => T } {
  let latest = initial
  let asOf = initial
  return {
    update: (value) => {
      latest = value
      if (!colleagueChanged || asOf === undefined) asOf = value
    },
    get: () => (colleagueChanged ? asOf : latest),
  }
}

/** `value` as the open edit is checked against (`trackAsOfEdit`), or the latest while none is open. */
export function useAsOfEditStart<T>(value: T): () => T {
  const [tracker] = useState(() => trackAsOfEdit(value))
  useLayoutEffect(() => tracker.update(value))
  return tracker.get
}
